import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Select,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuList,
  MenuItem,
  Popper,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { v4 as uuidv4 } from "uuid";

import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import colorConfigs from "../../../../configs/colorConfigs";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const ALLOWED_FILE_TYPES = ["pdf", "xls", "xlsx", "doc", "docx"];

// Attempt to get all supported timezones; otherwise, fall back to a sample list.
function getAllTimeZones() {
  if (typeof Intl !== "undefined" && Intl.supportedValuesOf) {
    return Intl.supportedValuesOf("timeZone");
  }
  return [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Paris",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Australia/Sydney",
  ];
}

const timeZones = getAllTimeZones();

const RequestPortingTime = (props) => {
  const { handleContinue, handleBack, products, data, loaSamples, ccase, t } =
    props;
  const { currentStep2 } = data;

  // ------------------ FILE STATES ------------------
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [loaFiles, setLoaFiles] = useState([]);
  const [isDraggingInvoice, setIsDraggingInvoice] = useState(false);
  const [isDraggingLoa, setIsDraggingLoa] = useState(false);

  // ------------------ NUMBER INPUTS STATES ------------------
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [numbers, setNumbers] = useState({});
  const [errors, setErrors] = useState({});
  const numberRefs = useRef({});

  // ------------------ LOA DROPDOWN STATES ------------------
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // ------------------ PREFERRED WINDOW ------------------
  const [preferredPortingWindow, setPreferredPortingWindow] = useState(
    t("SelectPortingWindow")
  );
  const [preferredWindowPayload, setPreferredWindowPayload] = useState(null);

  // ------------------ DIALOG STATES ------------------
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [timezone, setTimezone] = useState(dayjs.tz.guess());
  const [dateError, setDateError] = useState(null);
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [timezoneError, setTimezoneError] = useState("");
  const [windowError, setWindowError] = useState("");

  // ------------------ HOOKS AND INITIALIZATIONS ------------------
  // Convert file to base64
  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // result is base64 encoded
        const result = reader.result.split(",")[1]; // remove `data:application/pdf;base64,`
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // Get earliest date: if current time < 17 (5 PM), 7 days from now, else 8 days
  const getEarliestDate = () => {
    let earliest = dayjs();
    if (earliest.hour() < 17) {
      earliest = earliest.add(7, "day");
    } else {
      earliest = earliest.add(8, "day");
    }
    // Skip weekends
    while (earliest.day() === 0 || earliest.day() === 6) {
      earliest = earliest.add(1, "day");
    }
    return earliest.startOf("day");
  };

  const earliestDate = getEarliestDate();

  // Checks if the given date is a valid weekday (Mon-Fri) and not before earliestDate
  const shouldDisableDate = (date) => {
    if (date.isBefore(earliestDate, "day")) {
      return true;
    }
    // Disable Saturdays (6) and Sundays (0)
    if (date.day() === 0 || date.day() === 6) {
      return true;
    }
    return false;
  };

  // Min and max times (9 AM - 6 PM).
  // We compute them dynamically, especially for the earliest date.
  const getMinStartTime = () => {
    if (!selectedDate) return dayjs().hour(9).minute(0);

    // If the user selects exactly the earliest date, ensure the time is after "now" if "now" is between 9 AM and 6 PM
    const isEarliestDay = selectedDate.isSame(earliestDate, "day");
    if (isEarliestDay) {
      const now = dayjs();
      // For earliest date, the minimum is max(9 AM, now) if now < 6 PM
      const earliestPossible = selectedDate.hour(9).minute(0);
      if (now.isAfter(earliestPossible) && now.hour() < 18) {
        return now;
      }
      return earliestPossible;
    }
    // Otherwise just default to 9 AM
    return selectedDate.hour(9).minute(0);
  };

  const minStartTime = getMinStartTime();
  const maxTime = dayjs().hour(18).minute(0); // 6 PM

  // Time validation logic: must be 9 AM–6 PM. If earliest date chosen, must be > now if within the day.
  const disableInvalidTimes = (time, type) => {
    if (!selectedDate) return false;

    // 1) Must be between 9 AM and 6 PM
    if (time.hour() < 9 || time.hour() > 18 || (time.hour() === 18 && time.minute() > 0)) {
      return true;
    }

    // 2) If it's the earliest date, the startTime can't be before "now" if "now" is the same day
    const isEarliestDay = selectedDate.isSame(earliestDate, "day");
    if (isEarliestDay) {
      const now = dayjs();
      // If the earliest day is 'today + 7/8 days' but within the same day boundary,
      // check if time < now
      if (time.isBefore(now) && now.isSame(time, "day")) {
        return true;
      }
    }
    return false;
  };

  // ------------------ FILE UPLOAD HANDLERS ------------------
  const handleFileUpload = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();

    // Accept either e.target.files or e.dataTransfer.files
    let files = e.target?.files || e.dataTransfer?.files;
    if (!files) return;

    files = Array.from(files);

    // For each file, validate extension and then convert to base64
    const uploadedFiles = [];
    for (const file of files) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(extension)) {
        console.error(`File type .${extension} is not allowed.`);
        continue;
      }
      const b64file = await convertFileToBase64(file);
      uploadedFiles.push({
        filename: file.name,
        type: extension,
        b64file,
      });
    }

    if (type === "invoice") {
      setInvoiceFiles((prev) => [...prev, ...uploadedFiles]);
      setIsDraggingInvoice(false);
    } else if (type === "loa") {
      setLoaFiles((prev) => [...prev, ...uploadedFiles]);
      setIsDraggingLoa(false);
    }
  };

  const handleRemoveFile = (type, index) => {
    if (type === "invoice") {
      setInvoiceFiles((prev) => {
        const newArr = [...prev];
        newArr.splice(index, 1);
        return newArr;
      });
    } else if (type === "loa") {
      setLoaFiles((prev) => {
        const newArr = [...prev];
        newArr.splice(index, 1);
        return newArr;
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === "invoice") {
      setIsDraggingInvoice(true);
    } else if (type === "loa") {
      setIsDraggingLoa(true);
    }
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === "invoice") {
      setIsDraggingInvoice(false);
    } else if (type === "loa") {
      setIsDraggingLoa(false);
    }
  };

  // ------------------ NUMBER CHANGE HANDLER ------------------
  const handleNumberChange = (uniqueKey) => (e) => {
    const { value } = e.target;
    setNumbers((prevNumbers) => ({
      ...prevNumbers,
      [uniqueKey]: value,
    }));

    // Validate phone number
    const phoneNumberPattern = /^\+(\d{9,13})$/;
    if (!phoneNumberPattern.test(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [`number_${uniqueKey}`]:
        t('WarningPhoneNumber'),
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [`number_${uniqueKey}`]: "",
      }));
    }
  };

  // ------------------ USE EFFECTS ------------------
  // Initialize filteredProducts with uniqueKey
  useEffect(() => {
    let extractData;
    if (ccase === 4) {
      const { currentStep4 } = data;
      extractData = currentStep4;
    } else {
      const { currentStep3 } = data;
      extractData = currentStep3;
    }
    if (extractData) {
      const { invoiceFiles, loaFiles, filteredProducts, numbers, preferredPortingWindow, preferredWindowPayload } = extractData;
      setInvoiceFiles(invoiceFiles);
      setLoaFiles(loaFiles);
      if(preferredPortingWindow){
        setPreferredPortingWindow(preferredPortingWindow);
      }
      if(preferredWindowPayload){
        setPreferredWindowPayload(preferredWindowPayload);
      }      
      if (numbers) {
        setNumbers(numbers);
      }
      setFilteredProducts(filteredProducts);
    } else {
      const { portings } = currentStep2;
      const filtered = [];
      products.forEach((product) => {
        if (portings[product.id] > 0) {
          for (let i = 0; i < portings[product.id]; i++) {
            filtered.push({ ...product, uniqueKey: uuidv4() });
          }
        }
      });
      setFilteredProducts(filtered);
    }
  }, [ccase, products, currentStep2, data]);

  // ------------------ VALIDATIONS ------------------
  const validateNumbers = () => {
    let tempErrors = {};
    filteredProducts.forEach((product) => {
      const key = product.uniqueKey;
      const number = numbers[key];
      if (!number || number.trim() === "") {
        tempErrors[`number_${key}`] = "Phone number is required.";
      } else if (!/^\+(\d{9,13})$/.test(number)) {
        tempErrors[`number_${key}`] =
          t('WarningPhoneNumber');
      }
    });
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const validatePreferredWindow = () =>{
    if(!preferredWindowPayload){
      setWindowError("Error: Select Preferred Porting Window");
      return false;
    }
    if(windowError !== ""){
      setWindowError("");
    }
    return true;
  }

  // ------------------ DIALOG HANDLERS ------------------
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Validate the form inside the dialog
  const validateWindowForm = () => {
    let isValid = true;
    setDateError("");
    setStartTimeError("");
    setEndTimeError("");
    setTimezoneError("");

    if (!selectedDate) {
      setDateError("Date is required");
      isValid = false;
    }
    if (!startTime) {
      setStartTimeError("Start Time is required");
      isValid = false;
    }
    if (!endTime) {
      setEndTimeError("End Time is required");
      isValid = false;
    }
    if (!timezone) {
      setTimezoneError("Timezone is required");
      isValid = false;
    }

    // If both times exist, ensure endTime > startTime
    if (startTime && endTime && !endTime.isAfter(startTime)) {
      setEndTimeError("End Time must be after Start Time");
      isValid = false;
    }

    return isValid;
  };

  // When the user clicks "Save" on the dialog
  const handleDialogSave = () => {
    if (!validateWindowForm()) return;

    // Combine date + startTime + endTime in the chosen timezone
    // We'll create a start DateTime and an end DateTime in ISO8601
    const startDateTime = dayjs(selectedDate)
      .hour(startTime.hour())
      .minute(startTime.minute())
      .second(0)
      .tz(timezone, true);
    const endDateTime = dayjs(selectedDate)
      .hour(endTime.hour())
      .minute(endTime.minute())
      .second(0)
      .tz(timezone, true);

    // Format for the user-friendly "preferredPortingWindow"
    // e.g. "2025-01-08: 09:00 - 17:00"
    const dateStr = startDateTime.format("YYYY-MM-DD");
    const startStr = startDateTime.format("HH:mm");
    const endStr = endDateTime.format("HH:mm");
    const newPreferredWindow = `${dateStr}| ${startStr}-${endStr}`;
    setPreferredPortingWindow(newPreferredWindow);

    // Format the payload
    // e.g. {start_time: "2025-01-08T09:00:00+05:30", end_time: "2025-01-08T17:00:00+05:30"}
    const payload = {
      start_time: startDateTime.format(),
      end_time: endDateTime.format(),
    };
    setPreferredWindowPayload(payload);

    handleCloseDialog();
  };

  // ------------------ HANDLE CONTINUE ------------------
  const onContinue = () => {
    if(!validatePreferredWindow()){
      return;
    }
    if (!validateNumbers()) {
      console.error("Validation for phone numbers failed");
      return;
    }
    // Prepare data
    const portingData = filteredProducts.map((product) => ({
      productId: product.id,
      uniqueKey: product.uniqueKey,
      phoneNumber: numbers[product.uniqueKey],
    }));

    handleContinue({
      invoiceFiles,
      loaFiles,
      portingData,
      filteredProducts,
      numbers,
      preferredPortingWindow,
      preferredWindowPayload,
    });
  };

  // ------------------ LOA DROPDOWN LOGIC ------------------
  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    window.open(loaSamples[index].file, "_blank");
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMainButtonClick = () => {
    // Perform the default action (download the first file)
    window.open(loaSamples[selectedIndex].file, "_blank");
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // ------------------ RENDER ------------------
  return (
    <Box sx={{ p: 4, width: "65vw" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('Porting')}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('PortingMessage')}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="h6">{t('PreferredPortingWindow')}</Typography>
          <Box
            sx={{
              height: "5vh",
              width: "25vw",
              bgcolor: "#00ADD0",
              mr: 5,
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={handleOpenDialog} // <-- Make this clickable
          >
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: "white", p: 1 }}
            >
              {preferredPortingWindow}
            </Typography>
            <Box sx={{ p: 1, color: "white" }}>
              <KeyboardArrowDownIcon fontSize="medium" />
            </Box>
          </Box>
          {windowError && (
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ color: "red", p: 1 }}

          >{windowError}</Typography>)}
        </Box>
      </Box>

      {/* ------------------ PRODUCTS TABLE ------------------ */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#4BABCC" }}>
            <TableRow>
              <TableCell align="center">
                <Typography color={"white"} fontWeight="bold">
                  {t('Product')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography color={"white"} fontWeight="bold">
                  {t('PhoneNumber')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => {
              const key = product.uniqueKey;
              return (
                <TableRow key={key}>
                  <TableCell align="center">{product.name}</TableCell>
                  <TableCell align="center">
                    <TextField
                      id={`number-${key}`}
                      label="Phone Number"
                      type="text"
                      name={`number_${key}`}
                      value={numbers[key] || ""}
                      onChange={handleNumberChange(key)}
                      onBlur={handleNumberChange(key)}
                      variant="outlined"
                      size="small"
                      error={Boolean(errors[`number_${key}`])}
                      helperText={errors[`number_${key}`] || ""}
                      fullWidth
                      inputRef={(el) => (numberRefs.current[key] = el)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ------------------ FILE UPLOAD SECTIONS ------------------ */}
      <Grid container spacing={2}>
        {/* Invoice Upload Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            {t('Invoice')}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: isDraggingInvoice
                ? "2px dashed #4BABCC"
                : "1px solid #ccc",
              backgroundColor: isDraggingInvoice ? "#f0f8ff" : "transparent",
              minHeight: 100,
            }}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, "invoice")}
            onDragLeave={(e) => handleDragLeave(e, "invoice")}
            onDrop={(e) => handleFileUpload(e, "invoice")}
          >
            <IconButton color="primary" component="label">
              <FileUploadIcon sx={{ fontSize: 60, color: "lightgrey" }} />
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => handleFileUpload(e, "invoice")}
              />
              <Typography sx={{ ml: 1 }}>Drag here or select</Typography>
            </IconButton>
          </Paper>

          {/* Show uploaded invoice files */}
          {invoiceFiles.map((file, index) => (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #ccc",
                borderRadius: 1,
                p: 1,
              }}
              key={`invoice-${index}`}
            >
              <Typography variant="body2">{file.filename}</Typography>
              <IconButton
                onClick={() => handleRemoveFile("invoice", index)}
                color="error"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Grid>

        {/* LOA Upload Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            {t('LOA')}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: isDraggingLoa ? "2px dashed #4BABCC" : "1px solid #ccc",
              backgroundColor: isDraggingLoa ? "#f0f8ff" : "transparent",
              minHeight: 100,
            }}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, "loa")}
            onDragLeave={(e) => handleDragLeave(e, "loa")}
            onDrop={(e) => handleFileUpload(e, "loa")}
          >
            <IconButton color="primary" component="label">
              <FileUploadIcon sx={{ fontSize: 60, color: "lightgrey" }} />
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => handleFileUpload(e, "loa")}
              />
              <Typography sx={{ ml: 1 }}>{t('DragHere')}</Typography>
            </IconButton>
          </Paper>

          {/* Show uploaded LOA files */}
          {loaFiles.map((file, index) => (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #ccc",
                borderRadius: 1,
                p: 1,
              }}
              key={`loa-${index}`}
            >
              <Typography variant="body2">{file.filename}</Typography>
              <IconButton
                onClick={() => handleRemoveFile("loa", index)}
                color="error"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Grid>
      </Grid>

      {/* ------------------ ACTION BUTTONS ------------------ */}
      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* LOA Samples Dropdown */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ mr: 2, display: "inline-block" }}
          >
            {t('LOASamples')}
          </Typography>
          {loaSamples.length > 0 ? (
            <ButtonGroup
              variant="outlined"
              ref={anchorRef}
              aria-label="split button"
            >
              <Button onClick={handleMainButtonClick}>
                {`${loaSamples[selectedIndex].name} - ${loaSamples[selectedIndex].country}`}
              </Button>
              <Button
                size="small"
                aria-controls={open ? "split-button-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-label="select download"
                aria-haspopup="menu"
                onClick={handleToggle}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
          ) : (
            <Typography variant="body2" display="inline">
              {t('NoSamplesAvailable')}
            </Typography>
          )}
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu">
                      {loaSamples.map((option, index) => (
                        <MenuItem
                          key={option.id}
                          selected={index === selectedIndex}
                          onClick={(event) => handleMenuItemClick(event, index)}
                        >
                          <Typography variant="inherit">
                            {`${option.name} - ${option.country}`}
                          </Typography>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>

        {/* Navigation buttons */}
        <Box>
          <Button
            variant="text"
            sx={{ color: "#00AEEF", mr: 2 }}
            onClick={handleBack}
          >
            {t('Back')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ backgroundColor: "#00AEEF", color: "white" }}
            onClick={onContinue}
          >
            {t('Continue')}
          </Button>
        </Box>
      </Box>

      {/* ------------------ DIALOG FOR PREFERRED WINDOW ------------------ */}
      <Dialog 
       open={openDialog} 
       onClose={handleCloseDialog}
       sx={{
        "& .MuiDialog-paper": {
          width: "350px",   // or any custom value
          maxWidth: "none", // so it doesn't shrink to a default max-width
        },
       }}>
        <DialogTitle>{t('PreferredPortingWindow')}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Date */}
              <Grid item xs={12}>
                <DatePicker
                  label="Select Date *"
                  value={selectedDate}
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                    setStartTime(null);
                    setEndTime(null);
                  }}
                  shouldDisableDate={shouldDisableDate}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                    />
                  )}
                />
              </Grid>

              {/* Start Time */}
              <Grid item xs={12}>
                <TimePicker
                  label="Start Time *"
                  value={startTime}
                  onChange={(newValue) => setStartTime(newValue)}
                  disabled={!selectedDate}
                  minTime={minStartTime}
                  maxTime={maxTime}
                  shouldDisableTime={(timeValue, clockType) => {
                    let adjustedTime = dayjs(selectedDate).hour(0).minute(0);
                    if (clockType === "hours") {
                      adjustedTime = adjustedTime.hour(timeValue);
                    } else if (clockType === "minutes") {
                      adjustedTime = startTime
                        ? dayjs(selectedDate)
                            .hour(startTime.hour())
                            .minute(timeValue)
                        : dayjs(selectedDate).minute(timeValue);
                    }
                    return disableInvalidTimes(adjustedTime, "start");
                  }}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                    />
                  )}
                />
              </Grid>

              {/* End Time */}
              <Grid item xs={12}>
                <TimePicker
                  label="End Time *"
                  value={endTime}
                  onChange={(newValue) => setEndTime(newValue)}
                  disabled={!selectedDate}
                  minTime={startTime || minStartTime}
                  maxTime={maxTime}
                  shouldDisableTime={(timeValue, clockType) => {
                    let adjustedTime = dayjs(selectedDate).hour(0).minute(0);
                    if (clockType === "hours") {
                      adjustedTime = adjustedTime.hour(timeValue);
                    } else if (clockType === "minutes") {
                      adjustedTime = endTime
                        ? dayjs(selectedDate)
                            .hour(endTime.hour())
                            .minute(timeValue)
                        : dayjs(selectedDate).minute(timeValue);
                    }
                    if (startTime && adjustedTime.isBefore(startTime)) {
                      return true;
                    }
                    return disableInvalidTimes(adjustedTime, "end");
                  }}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                    />
                  )}
                />
              </Grid>

              {/* Timezone */}
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="timezone-label">Timezone *</InputLabel>
                  <Select
                    labelId="timezone-label"
                    label="Timezone *"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    error={Boolean(timezoneError)}
                  >
                    {timeZones.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </Select>
                  {timezoneError && (
                    <Typography variant="caption" color="error">
                      {timezoneError}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogSave} variant="contained" sx={{color: colorConfigs.btnStyle.color, background: colorConfigs.btnColor.bgColorGreen, }} fullWidth>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestPortingTime;
